import { NgClass, NgTemplateOutlet } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BOOKING_PAGE_VIEWS, GTM_EVENTS, MEETING_TYPE_ID, WIDGET_TEMPLATES_CONST } from '@constants/app.constants';
import { TEMPLATE_CONSTANT } from '@constants/template.constants';
import { environment } from '@environments/environment';
import { AppointmentServiceCategoryModel, AppointmentServiceModel } from '@models/appointment-service.model';
import { CurrentViewData } from '@models/widget-conf.model';
import { WidgetTemplateModel } from '@models/widget-template.model';
import { WorkerDbModel } from '@models/worker-db.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CalioCurrencyPipe } from '@pipes/calio-currency.pipe';
import { CalioMeetingTemplatePipe } from '@pipes/calio-meeting-template.pipe';
import { CalioSafePipe } from '@pipes/calio-safe.pipe';
import { CalioTranslationPipe } from '@pipes/calio-translation.pipe';
import { HourReplacePipe } from '@pipes/hour-replace.pipe';
import { MinuteReplacePipe } from '@pipes/minute-replace.pipe';
import { ReplaceCommaPipe } from '@pipes/replace-comma.pipe';
import { BookingService } from '@services/feature/booking.service';
import { CustomEventService } from '@services/feature/custom-event.service';
import { WidgetService } from '@services/feature/widget.service';
import { GoogleAnalyticsService } from '@services/utils/google-analytics.service';
import { HelperService } from '@services/utils/helper.service';
import { LoggerService } from '@services/utils/logger.service';
import { SwRouteService } from '@services/utils/sw-route.service';
import { AlertComponent } from '@ui-lib/alert/alert.component';
import { ButtonComponent } from '@ui-lib/buttons/button/button.component';
import { InfoCardComponent } from '@ui-lib/cards/info-card/info-card.component';
import { CswLinkComponent } from '@ui-lib/typography/csw-link/csw-link.component';
import { CswTextComponent } from '@ui-lib/typography/csw-text/csw-text.component';
import { catchError, forkJoin, of, switchMap } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { vatTypeLabelKey } from '@services/utils/vat-calculation.util';

const pipes = [CalioMeetingTemplatePipe, CalioCurrencyPipe, CalioSafePipe, CalioTranslationPipe, MinuteReplacePipe, HourReplacePipe, ReplaceCommaPipe];
const components = [AlertComponent, CswTextComponent, ButtonComponent, InfoCardComponent, CswLinkComponent];

@Component({
  selector: 'app-services-selection',
  templateUrl: './services-selection.component.html',
  styleUrls: ['./services-selection.component.scss'],
  imports: [NgTemplateOutlet, NgClass, TranslateModule, ...components, ...pipes],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ServicesSelectionComponent implements OnInit {
  protected readonly vatTypeLabelKey = vatTypeLabelKey;

  private customEventService = inject(CustomEventService);
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private helperService = inject(HelperService);
  private swRouteService = inject(SwRouteService);
  private translate = inject(TranslateService);
  protected bookingService = inject(BookingService);
  protected widgetService = inject(WidgetService);
  private destroyRef = inject(DestroyRef);

  protected readonly baseUrl = environment.imageHostName;
  protected readonly templateConstant = TEMPLATE_CONSTANT;

  private isGTMEventSent: boolean;
  /** Read FRESH, never cached: this mounts once, so an ngOnInit value pins to stale answers. */
  private get supportedAppointmentServiceIds(): number[] {
    return this.bookingService.getSupportedAppointmentServiceIdsFromLocalStorage(
      this.bookingService.partnerData?.booking_name
    );
  }
  protected showServiceGhostElement = false;
  protected showAdditionalServiceGhostElement = false;
  protected serviceGhostElements = new Array<number>(4);
  protected lang: string;
  protected serviceLabelTemplate: WidgetTemplateModel;
  protected additionalServiceLabelTemplate: WidgetTemplateModel;
  protected services: AppointmentServiceModel[];
  protected additionalServices: AppointmentServiceModel[];
  protected showErrorMessage = false;
  protected showServiceSelectionRequiredError = false;
  protected servicesGroupedData: {
    key: string,
    temp: {
      appointment_service_category: AppointmentServiceCategoryModel,
    },
    value: AppointmentServiceModel[],
  }[] = [];
  protected additionalServicesGroupedData: {
    key: string,
    temp: {
      appointment_service_category: AppointmentServiceCategoryModel,
    },
    value: AppointmentServiceModel[],
  }[] = [];

  @Output() nextPageEvent = new EventEmitter<CurrentViewData>();
  @Output() previousPageEvent = new EventEmitter<CurrentViewData>();

  constructor() {
    this.lang = this.translate.getFallbackLang();
    this.translate.onLangChange.subscribe(language => this.lang = language.lang);
  }

  ngOnInit(): void {
    this.isGTMEventSent = false;
    this.bookingService.loadAppointmentStateFromLocalStorage();
    if (this.bookingService.isMeetingTypeStepInFlow() && this.bookingService?.selectedBookingData?.currentSelectedAppointmentState?.selectedMeetingTypeId !== 1) {
      this.getAppointmentSevicesByMeetingTypes();
    } else {
      // show_meeting_type_picker: Meeting type picker is disabled; setting meeting provider to 1
      this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedMeetingTypeId = 1;
      this.getAppointmentSevicesByStoreMeetingType();
    }
    this.customEventService.toggleFillContainer.emit(true);
    this.setupTemplates();
  }

  private getAppointmentSevicesByMeetingTypes(): void {
    const meetingTypeId = this.bookingService?.selectedBookingData?.currentSelectedAppointmentState?.selectedMeetingTypeId;
    const storeId = this.widgetService.widgetConf?.context?.filter_meetingtype_services_based_on_store_id ? this.bookingService?.selectedBookingData?.currentSelectedAppointmentState?.selectedStoreId : undefined;
    this.showServiceGhostElement = true;
    this.bookingService.getAppointmentSevicesByMeetingType(meetingTypeId, storeId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => this.handelServices(result, meetingTypeId),
        error: (error: HttpErrorResponse) => this.handelServicesError(error)
      });
  }

  private getAppointmentSevicesByStoreMeetingType(): void {
    const storeId = this.bookingService?.selectedBookingData?.currentSelectedAppointmentState?.selectedStoreId;
    const selectedStoreUuids = this.bookingService?.selectedBookingData?.currentSelectedAppointmentState?.selectedStoreUuids;
    const workerId = this.bookingService?.selectedBookingData?.currentSelectedAppointmentState?.selectedWorkerId;
    const workerEmail = this.widgetService.widgetConf?.context?.worker_email;
    this.showServiceGhostElement = true;
    const meetingTypeId = this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedMeetingTypeId;

    this.bookingService.getAppointmentSevices({
      store_id: selectedStoreUuids?.length ? null : storeId,
      worker_id: workerId || null,
      worker_email: workerEmail || null,
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => this.handelServices(result, meetingTypeId),
        error: (error: HttpErrorResponse) => this.handelServicesError(error)
      });
  }

  private handelServices(result: AppointmentServiceModel[], meetingTypeId: number): void {
    if (result) {
      this.services = result;
      if (!this.widgetService.widgetConf.context.is_internal) {
        this.services = this.services?.filter(service => service.is_internal !== 1);
      }

      const filteredService = this.filterBySupportedServicesAndCategory(this.services);

      // show supported appointment service ids if present; in-case not present then show all the services
      filteredService.length > 0 && (this.services = filteredService);

      // When `worker_ids` is supplied via context, restrict services to those offered by at least
      // one of those workers — otherwise the user can pick a service their deep-linked workers
      // don't provide and dead-end at an empty worker step. Silent fallback to the unrestricted
      // list if the intersection is empty (mirrors the storeDefaultWorkerIds pattern).
      const contextWorkerIds = this.widgetService.widgetConf?.context?.worker_ids;
      if (contextWorkerIds?.length > 0 && this.services?.length) {
        const restricted = this.services.filter(service =>
          service.services_workers?.some(sw => contextWorkerIds.includes(sw.worker_id))
        );
        if (restricted.length > 0) {
          LoggerService.warn('[worker_ids] Restricting services to those offered by deep-linked workers:', contextWorkerIds);
          this.services = restricted;
        } else {
          LoggerService.warn('[worker_ids] Service intersection empty — falling back to unrestricted list');
        }
      }

      // When an advisor was pre-selected via the worker-select booking question, the customer has
      // explicitly chosen who they want to talk to — so the service list must show ONLY that advisor's
      // services. The meeting-type endpoint (used for non-store meeting types) returns every advisor's
      // services for that meeting type and does not filter by worker, so we restrict client-side here
      // using the services_workers payload. Mirrors the worker_ids block above. Silent fallback to the
      // unrestricted list if the advisor offers none of these services.
      const preSelectedWorkerId = this.bookingService?.selectedBookingData?.currentSelectedAppointmentState?.selectedWorkerId;
      if (
        this.bookingService?.selectedBookingData?.currentSelectedAppointmentState?.storeAndWorkerPreSelectedThroughWorkerSelectField &&
        preSelectedWorkerId > 0 &&
        this.services?.length
      ) {
        const restrictedToAdvisor = this.services.filter(service =>
          service.services_workers?.some(sw => sw.worker_id === preSelectedWorkerId)
        );
        if (restrictedToAdvisor.length > 0) {
          LoggerService.warn('[worker-select] Restricting services to the pre-selected advisor:', preSelectedWorkerId);
          this.services = restrictedToAdvisor;
        } else {
          LoggerService.warn('[worker-select] Pre-selected advisor offers none of these services — falling back to unrestricted list');
        }
      }

      LoggerService.log(this.bookingService?.selectedBookingData?.currentSelectedAppointmentState?.selectedMeetingTypeId);
      this.services?.forEach(service => service.meeting_provider_id = meetingTypeId);

      // Handle combi services when appointment_service_ids are passed via context
      if (this.widgetService.widgetConf?.context?.appointment_service_ids?.length > 0) {
        const preSelectedServiceIds = this.widgetService.widgetConf.context.appointment_service_ids;
        const preSelectedServices = this.services.filter(service => preSelectedServiceIds.includes(service.id));

        // If single service is pre-selected, keep it (whether combi or not) and update flag
        if (preSelectedServices.length === 1) {
          const singleService = preSelectedServices[0];
          if (singleService.is_combi === true) {
            this.bookingService.isCombiServiceSelected = true;
            LoggerService.log('[Debug] Single combi service passed via context. Keeping it and setting flag to true.');
          } else {
            this.bookingService.isCombiServiceSelected = false;
          }
        }
        // If multiple services are pre-selected, filter out combi services
        else if (preSelectedServices.length > 1) {
          const hasCombiService = preSelectedServices.some(service => service.is_combi === true);
          if (hasCombiService) {
            // Remove combi services from the pre-selected list
            const filteredServiceIds = preSelectedServices
              .filter(service => service.is_combi !== true)
              .map(service => service.id);

            if (filteredServiceIds.length > 0) {
              this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedServiceIds = filteredServiceIds;
              this.widgetService.widgetConf.context.appointment_service_ids = filteredServiceIds;
              // Reset flag since we filtered out combi services
              this.bookingService.isCombiServiceSelected = false;
              LoggerService.warn('[Debug] Filtered out combi services from pre-selected services. Only simple services will be used.');
            } else {
              // All services were combi services, clear selection
              this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedServiceIds = [];
              this.widgetService.widgetConf.context.appointment_service_ids = [];
              this.bookingService.isCombiServiceSelected = false;
              LoggerService.warn('[Debug] All pre-selected services were combi services. Cleared selection.');
            }
          } else {
            // No combi services in the selection
            this.bookingService.isCombiServiceSelected = false;
          }
        }

        // [auto_load_booking_questions] Context-preselected/combi path — resolve once the
        // service ids for this load are settled. No-op when the flag is off (AC3/AC5).
        this.bookingService.resolveAutoLoadedBookingQuestions(
          this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedServiceIds
        );
      }

      this.servicesGroupedData = this.setupAppointmentServiceCategories(this.services);
      this.showServiceGhostElement = false;

      // [hidden_booking_steps] SERVICES hidden → the answer's supported_appointment_service_ids must
      // have narrowed the list to exactly ONE service; force-select it and skip in the travel
      // direction. If the answer maps to 0 or >1 services the value can't resolve → render normally.
      // onSelect(..., false) sets state WITHOUT auto-navigating (its auto-next is gated on
      // isUserInteracted), so we drive next()/previous() explicitly here.
      if (
        this.bookingService.isStepHidden(BOOKING_PAGE_VIEWS.SERVICES_VIEW) &&
        // Already failed to resolve once → it's being rendered as a real step; don't force-skip again.
        !this.bookingService.hiddenStepResolutionFailed.has(BOOKING_PAGE_VIEWS.SERVICES_VIEW)
      ) {
        if (this.services?.length === 1) {
          this.clearForcedServiceSelection();
          this.onSelect(this.services[0], false);
          if (this.bookingService.isNavigatingBackTo(BOOKING_PAGE_VIEWS.SERVICES_VIEW)) {
            this.previous();
          } else {
            this.next(false);
          }
        } else {
          this.bookingService.markHiddenStepUnresolved(BOOKING_PAGE_VIEWS.SERVICES_VIEW);
        }
        return;
      }

      // Check if user is navigating back from a later step (workers or slots).
      // In that case, don't auto-navigate — let them stay on services to change their selection.
      const isNavigatingBack = (
        this.bookingService.lastView === BOOKING_PAGE_VIEWS.WORKERS_VIEW ||
        this.bookingService.lastView === BOOKING_PAGE_VIEWS.SLOTS_VIEW
      );

      if (this.services?.length === 1) {
        this.onSelect(this.services[0], false);
        if (!isNavigatingBack) {
          // TODO (Chirag): Need to check why we have different behavior for different meeting types
          if (meetingTypeId === MEETING_TYPE_ID.STORE) {
            // check if service is pre-selected via URL param then skip step even though auto skip is disabled
            if (this.swRouteService.isAutoSkipStepsEnabled() || this.widgetService.isDefaultAppointmentServiceIdsSelected()) {
              this.next(false);
            }
          } else {
            this.next(false);
          }
        }
      } else if (
        this.services?.length > 1 &&
        this.bookingService.selectedBookingData.currentSelectedAppointmentState?.selectedServiceIds?.length > 0
      ) {

        // Check if findService exists in the service list; if yes, proceed further,
        // otherwise remove the previously selected services.
        const findService = this.services.find(service => this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedServiceIds?.indexOf(service.id) > -1);
        if (findService) {
          this.onSelect(findService, false);
        } else {
          this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedServiceIds = Object.assign([], []);
          this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedServices = Object.assign([], []);
          this.bookingService.selectedBookingData.selectedServices = Object.assign([], []);
          // Reset isCombiServiceSelected flag when services are cleared
          this.bookingService.isCombiServiceSelected = false;
        }

        if (this.bookingService.selectedBookingData?.selectedServices?.length > 0) {
          if (isNavigatingBack) {
            // User explicitly navigated back — only auto-skip if services
            // are also pre-selected via widget configuration (true pass-through step)
            if (this.widgetService.isDefaultAppointmentServiceIdsSelected()) {
              this.previous();
            }
          } else {
            // Forward navigation — auto-advance to next step
            this.next(false);
          }
        }
      }

      // Check for show_additional_services_provided_by_meeting_providers
      if (this.widgetService.widgetConf?.context?.show_additional_services_provided_by_meeting_providers?.length) {
        if (this.bookingService.partnerData.allow_multiple_services_bookings !== 1) {
          this.checkforAdditionalServicesSuggestion(this.widgetService.widgetConf.context.show_additional_services_provided_by_meeting_providers);
        } else {
          LoggerService.warn('[debug] show_additional_services_provided_by_meeting_providers will only work if allow_multiple_services_bookings is disabled');
        }
      }
    } else {
      // TODO show error if services are not there
      this.showServiceGhostElement = false;
      // [hidden_booking_steps] Null/empty service result for a hidden SERVICES step → un-hide it so
      // the user isn't stranded on an invisible display:none section (never a blank dead-end).
      if (this.bookingService.isStepHidden(BOOKING_PAGE_VIEWS.SERVICES_VIEW)) {
        this.bookingService.markHiddenStepUnresolved(BOOKING_PAGE_VIEWS.SERVICES_VIEW);
      }
    }
  }

  private handelServicesError(error: HttpErrorResponse): void {
    this.googleAnalyticsService.emitAppointmentBookingEvent(GTM_EVENTS.appointment_booking_step_failed);
    this.bookingService.gtmStartTriggered = false;
    this.showServiceGhostElement = false;
    this.showErrorMessage = true;
    // [hidden_booking_steps] A transport failure fetching services must NOT leave a hidden SERVICES
    // step invisibly stuck — un-hide it so the error message becomes visible and the flow recovers.
    if (this.bookingService.isStepHidden(BOOKING_PAGE_VIEWS.SERVICES_VIEW)) {
      this.bookingService.markHiddenStepUnresolved(BOOKING_PAGE_VIEWS.SERVICES_VIEW);
    }
    LoggerService.error(error);
  }

  private filterBySupportedServicesAndCategory(services: AppointmentServiceModel[]): AppointmentServiceModel[] {
    const category = this.widgetService.widgetConf.context?.category;
    // Once per pass, not per service: the getter decrypts localStorage on every access.
    const supportedIds = this.supportedAppointmentServiceIds;
    // filter supported appointment service ids from all ther services
    return services.filter(service => {
      // Check if supportedAppointmentServiceIds has values, and if so, filter by it
      const isSupported = supportedIds.length === 0 || supportedIds.includes(service.id);
      // Check if category is provided and has values, then filter by it
      const isInCategory = !category || category?.length === 0 || category.includes(service.appointment_service_category?.id);
      return isSupported && isInCategory;
    });
  }

  private setupAppointmentServiceCategories(services: AppointmentServiceModel[]) {
    const groupedData = this.helperService.groupBy(
      services,
      'appointment_service_category_id',
      'position',
      'position',
      'appointment_service_category'
    );

    // Sort finalDataList based on temp.appointment_service_category.position
    groupedData.sort((a, b) => {
      const posA = a.temp?.appointment_service_category?.position ?? Number.MAX_VALUE;
      const posB = b.temp?.appointment_service_category?.position ?? Number.MAX_VALUE;
      return posA - posB;
    });

    return groupedData;
  }

  protected next(isUserInteracted = true): void {
    // Service is is not selected and clicked on next then show error.
    if (this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedServiceIds.length === 0) {
      this.showServiceSelectionRequiredError = true;
      return;
    }
    isUserInteracted && this.googleAnalyticsService.emitAppointmentBookingEvent(GTM_EVENTS.appointment_booking_step_services_end);
    this.bookingService.setAppointmentStateInLocalStorage(this.bookingService.selectedBookingData.currentSelectedAppointmentState);
    this.customEventService.toggleFillContainer.emit(false);

    // If combi service is selected, skip worker selection and set worker to 0 (Anyone)
    if (this.bookingService.isCombiServiceSelected) {
      this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedWorkerId = 0;
      this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedWorker = new WorkerDbModel();
      this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedWorker.id = 0;
      this.bookingService.setAppointmentStateInLocalStorage(this.bookingService.selectedBookingData.currentSelectedAppointmentState);
      LoggerService.log('[Debug] Combi service selected. Skipping worker selection and setting worker to 0 (Anyone).');
      this.nextPageEvent.emit({ view: BOOKING_PAGE_VIEWS.SLOTS_VIEW, isUserInteracted });
    } else {
      this.nextPageEvent.emit({ view: BOOKING_PAGE_VIEWS.WORKERS_VIEW, isUserInteracted });
    }
  }

  protected previous(): void {
    this.isGTMEventSent && this.googleAnalyticsService.emitAppointmentBookingEvent(GTM_EVENTS.appointment_booking_step_services_end);
    this.customEventService.toggleFillContainer.emit(false);
    const selectedMeetingTypeId = this.bookingService?.selectedBookingData?.currentSelectedAppointmentState?.selectedMeetingTypeId;
    const context = this.widgetService?.widgetConf?.context;

    // Mirror the forward navigation logic from meeting-type-selection.next():
    // Go back to STORE_VIEW only if the store step was part of the forward flow.
    // Otherwise go back to MEETING_TYPE_VIEW (or STORE_VIEW if no meeting type picker).
    if (this.bookingService.isMeetingTypeStepInFlow()) {
      const storeWasInForwardFlow = (
        selectedMeetingTypeId === MEETING_TYPE_ID.STORE ||
        (selectedMeetingTypeId === MEETING_TYPE_ID.CUSTOMER && context?.show_store_selection_for_customer_meeting_type)
      );

      if (storeWasInForwardFlow) {
        this.previousPageEvent.emit({ view: BOOKING_PAGE_VIEWS.STORE_VIEW, isUserInteracted: true });
      } else {
        this.previousPageEvent.emit({ view: BOOKING_PAGE_VIEWS.MEETING_TYPE_VIEW, isUserInteracted: true });
      }
    } else {
      this.previousPageEvent.emit({ view: BOOKING_PAGE_VIEWS.STORE_VIEW, isUserInteracted: true });
    }
  }

  protected toggleServiceSelection(serviceData: AppointmentServiceModel): void {
    if (this.isSelectedService(serviceData)) {
      this.onDeSelect(serviceData);
    } else {
      this.onSelect(serviceData);
    }
  }

  /**
   * Drops the previously force-selected service so the newly resolved one REPLACES it.
   *
   * onSelect() removes an existing entry only when its id MATCHES, then APPENDS when the partner
   * has `allow_multiple_services_bookings = 1` — right for a customer-built basket, wrong for a
   * resolution they never see: re-answering would keep the abandoned branch's service and book
   * BOTH, with slots the SUM of their durations (60 + 15 = 75-minute slots).
   *
   * Only reachable while SERVICES is hidden and exactly one service resolved, so there is never
   * a customer-built basket here.
   */
  private clearForcedServiceSelection(): void {
    this.bookingService.selectedBookingData.selectedServices = [];

    const state = this.bookingService.selectedBookingData?.currentSelectedAppointmentState;
    if (state) {
      state.selectedServices = [];
      state.selectedServiceIds = [];
    }
  }

  private onSelect(serviceData: AppointmentServiceModel, isUserInteracted = true): void {
    // Reset Flag
    this.showServiceSelectionRequiredError = false;

    if (isUserInteracted) {
      // trigger start event on first user interaction happen and
      // service selection selection step is configured to first step but
      // only single service selection is present
      this.googleAnalyticsService.triggerApoointmentStartEvent();

      if (!this.isGTMEventSent) {
        this.googleAnalyticsService.emitAppointmentBookingEvent(GTM_EVENTS.appointment_booking_step_services_start);
        this.isGTMEventSent = true;
      }
    }

    // If the newly selected service already exists in the selected services list,
    // remove it and reselect it with the new worker.
    const existingServiceIndex = this.bookingService.selectedBookingData.selectedServices.findIndex(service => service.id === serviceData.id);
    if (existingServiceIndex > -1) {
      this.bookingService.selectedBookingData.selectedServices.splice(existingServiceIndex, 1);
    }
    this.bookingService.selectedBookingData.selectedServices.push(serviceData);

    if (this.bookingService.partnerData.allow_multiple_services_bookings === 1) {
      if (existingServiceIndex > -1) {
        this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedServices.splice(existingServiceIndex, 1);
        this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedServiceIds.splice(existingServiceIndex, 1);
      }
      this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedServiceIds.push(serviceData.id);
      this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedServices.push(serviceData);
    } else {
      this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedServiceIds = Object.assign([], [serviceData.id]);
      this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedServices = Object.assign([], [serviceData]);
    }
    this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedMeetingTypeId = serviceData.meeting_provider_id;

    // Update isCombiServiceSelected flag based on selected services
    this.bookingService.isCombiServiceSelected = this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedServices.some(service => service.is_combi === true);

    // [auto_load_booking_questions] No-op when the flag is off (AC3/AC5); idempotent re-resolve
    // on every select otherwise (AC6).
    this.bookingService.resolveAutoLoadedBookingQuestions(this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedServiceIds);

    LoggerService.log('[debug] currentSelectedAppointmentState ', this.bookingService.selectedBookingData.currentSelectedAppointmentState);
    if (
      isUserInteracted &&
      (
        this.widgetService.widgetConf?.context?.enable_one_page_ui ||
        this.widgetService.widgetConf?.context?.auto_step_forward_on_select
      )
    ) {
      // Proceeds to the next step if the one-page UI is enabled.
      // Or if auto step forward on select is enabled.
      this.next();
    }
  }

  private onDeSelect(serviceData: AppointmentServiceModel): void {
    const serviceIndex = this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedServiceIds?.indexOf(serviceData.id);
    this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedServiceIds.splice(serviceIndex, 1);
    this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedServices.splice(serviceIndex, 1);

    // Update isCombiServiceSelected flag based on remaining selected services
    this.bookingService.isCombiServiceSelected = this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedServices.some(service => service.is_combi === true);

    // [auto_load_booking_questions] Re-resolve for the remaining set (AC6); no-op when off.
    this.bookingService.resolveAutoLoadedBookingQuestions(this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedServiceIds);

    LoggerService.log('[debug] currentSelectedAppointmentState ', this.bookingService.selectedBookingData.currentSelectedAppointmentState);
  }

  private setupTemplates(): void {
    this.serviceLabelTemplate = this.bookingService.widgetTemplates.find(template => template.identifier === WIDGET_TEMPLATES_CONST.WIDGET_APPOINTMENT_SERVICE_LABEL);
    this.serviceLabelTemplate && (this.serviceLabelTemplate.is_multi_language = 1);

    this.additionalServiceLabelTemplate = this.bookingService.widgetTemplates.find(template => template.identifier === WIDGET_TEMPLATES_CONST.ADDITIONAL_SERVICES_BY_ANOTHER_MEETING_PROVIDER);
    this.additionalServiceLabelTemplate && (this.additionalServiceLabelTemplate.is_multi_language = 1);
  }

  private checkforAdditionalServicesSuggestion(supportedAdditionalMeetingProviderIds: number[]): void {
    const supportedMeetingProviderIds = [...this.widgetService.widgetConf?.context?.meeting_providers];

    // Remove selected Meeting type from list
    supportedMeetingProviderIds?.splice(supportedMeetingProviderIds.indexOf(this.bookingService?.selectedBookingData?.currentSelectedAppointmentState?.selectedMeetingTypeId), 1);

    // Filter supportedAdditionalMeetingProviderIds by context meeting_providers
    const filteredAdditionalMeetingProviderIds = supportedAdditionalMeetingProviderIds.filter(id => supportedMeetingProviderIds.includes(id));

    if (filteredAdditionalMeetingProviderIds.length) {
      this.showAdditionalServiceGhostElement = true;
      const additionalServices: Observable<AppointmentServiceModel[]>[] = [];

      // Check if meeting provider is store
      if (filteredAdditionalMeetingProviderIds.includes(1)) {
        const storeId = this.bookingService?.selectedBookingData?.currentSelectedAppointmentState?.selectedStoreId;
        const selectedStoreUuids = this.bookingService?.selectedBookingData?.currentSelectedAppointmentState?.selectedStoreUuids;
        const workerId = this.bookingService?.selectedBookingData?.currentSelectedAppointmentState?.selectedWorkerId;
        const workerEmail = this.widgetService.widgetConf?.context?.worker_email;
        this.showServiceGhostElement = true;

        additionalServices.push(this.bookingService.getAppointmentSevices({
          store_id: selectedStoreUuids?.length ? null : storeId,
          worker_id: workerId || null,
          worker_email: workerEmail || null,
        })
          .pipe(
            switchMap(services => {
              return of(services?.map(service => {
                service.meeting_provider_id = 1;
                service.is_additional_service = 1;
                return service;
              }));
            }),
            catchError(() => of([]))
          ));
        filteredAdditionalMeetingProviderIds?.splice(filteredAdditionalMeetingProviderIds.indexOf(1), 1);
      }

      //Check for other meeting providers
      if (filteredAdditionalMeetingProviderIds.length) {
        filteredAdditionalMeetingProviderIds.forEach(meetingProviderId => {
          additionalServices.push(this.bookingService.getAppointmentSevicesByMeetingType(meetingProviderId)
            .pipe(
              switchMap(services => {
                return of(services?.map(service => {
                  service.meeting_provider_id = meetingProviderId;
                  service.is_additional_service = 1
                  return service;
                }));
              }),
              catchError(() => of([]))
            ));
        });
      }

      forkJoin(additionalServices).subscribe(
        result => {
          let tempServices = Array<AppointmentServiceModel>();
          result.forEach(services => {
            services.length && tempServices.push(...services);
          });

          if (!this.widgetService.widgetConf.context.is_internal) {
            tempServices = tempServices.filter(service => service.is_internal !== 1);
          }

          tempServices = this.filterBySupportedServicesAndCategory(tempServices);

          // [SANITAS] To remove duplicate services based on the same name
          const defaulServiceNames = this.services?.map(service => service?.name?.trim()?.toLowerCase());
          tempServices = tempServices.filter(service => !defaulServiceNames?.includes(service?.name?.trim()?.toLowerCase()));

          this.additionalServicesGroupedData = this.setupAppointmentServiceCategories(tempServices);
          this.showAdditionalServiceGhostElement = false;
        }
      );

    } else {
      LoggerService.warn('[Debug:show_additional_services_provided_by_meeting_providers] No additional meeting types found for service suggestions.');
    }
  }

  isSelectedService(serviceData: AppointmentServiceModel): boolean {
    const findIndex = this.bookingService.selectedBookingData.currentSelectedAppointmentState?.selectedServices.findIndex(service => service.id === serviceData.id && (service.meeting_provider_id === serviceData.meeting_provider_id))
    return this.bookingService.selectedBookingData.currentSelectedAppointmentState?.selectedServices?.[findIndex]?.meeting_provider_id === serviceData.meeting_provider_id
  }
}
